import { Dialog, Transition } from "@headlessui/react";
import { ArrowRightIcon, XIcon } from "@heroicons/react/outline";
import { CrudFilters, useList, useTranslation } from "@refinedev/core";
import { FullScreenLoading } from "components/fullScreenLoading";
import Pagination from "components/pagination";
import { IHistoryLogItem } from "interfaces";
import { capitalize, get, isBoolean, isObject } from "lodash";
import moment from "moment";
import { Fragment, useEffect, useRef, useState } from "react";
import { CONTACT_STATUS, CURRENCIES, PROVINCE, TIMEZONES } from "utills/constants";
import { formatNumber, isHTML } from "utills/helpers";
import Linkify from 'react-linkify';

interface HistoryLogModalProps {
  open: boolean;
  id?: string;
  customFilters?: CrudFilters;
  onClose: () => void;
  resource: string;
  lang?: string;
}

export const HistoryLodModal = ({
  open,
  onClose,
  resource,
  id,
  customFilters,
  lang
}: HistoryLogModalProps) => {
  const { translate } = useTranslation();

  const [trigger, setTrigger] = useState(false);
  const [initoading, setInitLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: "1",
  });
  const dialogRef = useRef(null);

  const {
    data: logData,
    refetch,
    isLoading,
    isFetching,
    isPreviousData
  } = useList<IHistoryLogItem>({
    resource,
    pagination: {
      mode: "off",
      pageSize: 5,
    },
    meta: {
      resource: "data",
      filters: ([{ field: "page", operator: "eq", value: filters.page }] as CrudFilters).concat(customFilters || []),
    },
    queryOptions: {
      enabled: false,
    } as any,
    successNotification: (resource) => {
      if (initoading) {
        setInitLoading(false);
      }
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    },
    errorNotification: (response) => {
      if (initoading) {
        setInitLoading(false);
      }
      return {
        message: `Error ${resource}.`,
        description: "Error",
        type: "error",
      }
    }
  });

  useEffect(() => {
    if (trigger) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, filters]);

  useEffect(() => {
    if (open) {
      setTrigger(true);
    } else {
      _initState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const _handleFilterChange = (key: string, value: any) => {
    const updatedFilter = {
      [key]: value,
      ...(key !== "page" && { page: "1" }),
    };
    setFilters({
      ...filters,
      ...updatedFilter,
    });
  };

  const _initState = () => {
    setTrigger(false);
    setInitLoading(true);
    setFilters({
      page: "1"
    })
  }

  const _renderLogValue = (value: any, key: string, id?:string) => {
    
    if (isBoolean(value)) return value ? "Yes" : "No";
    if (Number.isFinite(value)) return formatNumber(value);
    if (id === "blog") {
      if (key === "Category") return capitalize(value ?? "");
    }
    if (id === "cs-contact") {
      return CONTACT_STATUS.find((contact) => contact.value === value)?.label ?? "N/A"
    }
    if (id === "event-step") {
      if (key === "Venue") {
        return PROVINCE.find((province) => province.value === value)?.name_en ?? "N/A"
      }
      if (key === "Currency") {
        return CURRENCIES.find((currency) => currency.value === value)?.label ?? "N/A"
      }
      if (key === "Timezone") {
        return get(TIMEZONES, `${value}.value`, "N/A")
      }
    }
    if (isObject(value)) {
      return Object.keys(value).map((key: string, index: number) => (
        <div className="mt-1 ml-3" key={index}>
          <p className="font-medium">{key}</p>
          <p>{_renderLogValue(get(value, key, ""), key)}</p>
        </div>
      ))
    }
    if (isHTML(value)) {
      return (
        <div className="overflow-hidden reset-tw">
          <div 
            dangerouslySetInnerHTML={{
              __html: `${value ?? ""}`,
          }} />
        </div>
      )
    }
    return ((value === null || value === "") ? "N/A" : (value ?? "")).toString();
  };

  if ((isLoading || (isFetching && !isPreviousData) || initoading) && open) {
    return <FullScreenLoading />
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose} initialFocus={dialogRef ? dialogRef : undefined}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[60] w-screen overflow-y-auto" ref={dialogRef}>
          <div className="flex h-dvh items-center justify-center p-4 box-border">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden max-h-full flex flex-col rounded-lg bg-white text-left shadow-xl transition-all w-[500px] max-w-full sm:w-fit sm:min-w-[500px] sm:max-w-full ">
                <div className="flex items-center justify-between px-4 py-3.5 border-b rounded-t">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {lang ? translate("action_history_log") : "Action History Log"}
                  </h3>
                  <span
                    className="bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 border-0 ms-auto inline-flex justify-center items-center ring-transparent cursor-pointer"
                    data-modal-hide="static-modal"
                    onClick={onClose}
                  >
                    <XIcon width={20} height={20} />
                  </span>
                </div>
                <div className="divide-y divide-gray-100 text-sm overflow-y-auto">
                  {!logData?.data?.length && !isLoading && (
                    <div className="text-center box-container text-base">
                      {lang ? translate("no_data") : "No data"}
                    </div>
                  )}

                  {logData?.data.map((log: IHistoryLogItem, index: number) => (
                    <div
                      key={index}
                      className="max-w-full px-4 py-4 sm:px-6 flex flex-col md:flex-row gap-4 md:gap-6"
                    >
                      <div className="shrink-0 w-44">
                        <p className="break-all">{log.username}</p>
                        <p>
                          {log.created_at
                            ? moment(log.created_at).format(
                                "DD/MM/YYYY HH:mm:ss"
                              )
                            : ""}
                        </p>
                      </div>
                      <div className="flex-1 max-w-full">
                        <p className="font-medium text-brand-500">
                          {log.message}
                        </p>
                        <div>
                          {Object.keys(log.data).map((key: string) => (
                            <div
                              key={key}
                              className="[&:not(:first-child)]:mt-1"
                            >
                              <p className="font-medium">{key}:</p>
                              <div className="break-words">
                              <Linkify 
                                componentDecorator={(decoratedHref, decoratedText, key) => (
                                  <a target="_blank" rel="noreferrer" href={decoratedHref} key={key}>
                                    {decoratedText}
                                  </a>
                                )}
                              >
                                <div className="inline break-all" style={{wordBreak: "break-word"}}>
                                  {_renderLogValue(log.data[key].from, key, id)}{" "}
                                </div>
                                <ArrowRightIcon className="w-3 h-3 inline mx-1" />{" "}
                                <div className="inline break-all" style={{wordBreak: "break-word"}}>
                                  {_renderLogValue(log.data[key].to, key, id)}{" "}
                                </div>
                              </Linkify>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {logData?.paging && logData.paging.total_page > 1 ? (
                  <div className="px-4 py-6 sm:px-6 w-full flex justify-center items-center border-t">
                    <Pagination
                      hideTotal
                      currentPage={logData?.paging.current_page}
                      totalPage={logData?.paging.total_page}
                      total={{
                        totalItems: logData?.paging.total_item || 0,
                        total: logData?.paging.total,
                      }}
                      onChange={(page: number) =>
                        _handleFilterChange("page", page)
                      }
                    />
                  </div>
                ) : (
                  <></>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
